/**
 * Report · Assessment Results — V109-6-5 / ADR-0091 (program Report stage).
 *
 * One list of the program's signed OSCAL exports built from a workspace sweep (Assessment Results
 * for all controls, the POA&M), each with Download (the signed JSON) and Verify (the public signed
 * snapshot). The program lead or an owner / admin generates from the latest finished sweep; with no
 * sweep yet the panel offers to start one (admins) — never a dead end.
 *
 * Reads: GET …/assessment-results (any member). Writes: POST …/assessment-results (lead / admin),
 * POST /scans (owner / admin). DS components and tokens only.
 */
import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, EmptyState, Spinner } from '@aigovops/design-system';
import { ApiError } from '../../lib/api';
import {
  downloadAssessmentExport, generateAssessmentExport, listAssessmentExports, startWorkspaceSweep, type AssessmentExport, type AssessmentExportKind,
} from '../../lib/assessment-results-api';
import { fmtProgramDate } from '../../lib/program-shell';
import { ASSESSMENT_RESULTS_ANCHOR } from '../../lib/sweep-model';

const KIND_LABEL: Record<AssessmentExportKind, string> = {
  oscal_ar_program: 'Assessment results · all controls',
  oscal_ar_control: 'Assessment results · one control',
  oscal_poam: 'Plan of action and milestones',
};

const exportTitle = (x: AssessmentExport) =>
  x.kind === 'oscal_ar_control' && x.scope.control_ids[0] ? `Assessment results · ${x.scope.control_ids[0].toUpperCase()}` : KIND_LABEL[x.kind];

function errorWords(e: unknown): string {
  if (e instanceof ApiError) {
    const code = (e.body as { code?: string } | null)?.code;
    if (code === 'invalid_export') return 'The export failed validation and was not stored. Try again after the next sweep, or contact support.';
    if (code === 'no_sweep') return 'No finished sweep yet. Run a sweep first.';
    if (e.status === 403) return 'Only the program lead or an admin can generate exports.';
  }
  return e instanceof Error ? e.message : 'Something went wrong.';
}

export function AssessmentResults({ slug, program, isAdmin }: { slug: string; program: string; isAdmin: boolean }) {
  const qc = useQueryClient();
  const key = ['assessment-results', slug, program];
  const q = useQuery({ queryKey: key, queryFn: () => listAssessmentExports(slug, program), retry: false });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // The heading is the roll-up's "Assessment results" anchor (ASSESSMENT_RESULTS_ANCHOR): render it while loading too.
  if (q.isLoading) return <Panel><Spinner label="Loading assessment results" /></Panel>;
  if (q.error || !q.data) return <Panel><Alert variant="error"><p>Assessment results could not load.</p><Button onClick={() => void q.refetch()}>Retry</Button></Alert></Panel>;
  const { latest_sweep: sweep, can_generate: canGenerate, exports } = q.data;

  const run = async (label: string, fn: () => Promise<unknown>, done: string) => {
    if (busy) return;
    setBusy(label); setError(null); setNotice(null);
    try {
      await fn();
      setNotice(done);
      await qc.invalidateQueries({ queryKey: key });
    } catch (e) { setError(errorWords(e)); } finally { setBusy(null); }
  };
  const generate = (kind: AssessmentExportKind) => run(kind, () => generateAssessmentExport(slug, program, { kind }), `${KIND_LABEL[kind]} generated and signed.`);
  const download = (x: AssessmentExport) => run(`dl-${x.package_id}`, () => downloadAssessmentExport(x.download_path), `${exportTitle(x)} downloaded.`);

  return (
    <Panel>
      <p className="gp-tab-sub" role="status" aria-live="polite">{notice ?? ''}</p>
      {error ? <Alert variant="error"><p>{error}</p></Alert> : null}
      {!sweep ? (
        <EmptyState
          title="No finished sweep yet"
          description="Results are built from one sweep of your connected sources."
          action={isAdmin
            ? <Button variant="primary" disabled={!!busy} onClick={() => void run('sweep', () => startWorkspaceSweep(slug), 'Sweep started. Generate results when it finishes.')}>{busy === 'sweep' ? 'Starting…' : 'Run a sweep'}</Button>
            : <p className="gp-dim">An admin starts a sweep.</p>}
        />
      ) : (
        <>
          <p className="gp-tab-sub">{`Last sweep: ${fmtProgramDate(sweep.finished_at)} · ${sweep.state}`}</p>
          {canGenerate ? (
            <div className="gp-actions">
              <Button variant="primary" disabled={!!busy} onClick={() => void generate('oscal_ar_program')}>{busy === 'oscal_ar_program' ? 'Generating…' : 'Generate assessment results'}</Button>
              <Button disabled={!!busy} onClick={() => void generate('oscal_poam')}>{busy === 'oscal_poam' ? 'Generating…' : 'Generate POA&M'}</Button>
            </div>
          ) : null}
        </>
      )}
      {exports.length ? (
        <ul className="gp-gap-list" aria-label="Signed exports">
          {exports.map((x) => (
            <li key={x.package_id} className="gp-gap">
              <span><strong>{exportTitle(x)}</strong>{` · generated ${fmtProgramDate(x.created_at)}`}</span>
              <span className="gp-actions">
                <Button compact disabled={!!busy} onClick={() => void download(x)} aria-label={`Download ${exportTitle(x)}, ${fmtProgramDate(x.created_at)}`}>
                  {busy === `dl-${x.package_id}` ? 'Downloading…' : 'Download'}
                </Button>
                <a href={x.verify_path} target="_blank" rel="noopener noreferrer" aria-label={`Verify ${exportTitle(x)}, ${fmtProgramDate(x.created_at)} (opens in a new tab)`}>Verify</a>
              </span>
            </li>
          ))}
        </ul>
      ) : sweep ? (
        <p className="gp-dim">{canGenerate ? 'No export yet · generate one from the latest sweep.' : 'No export yet · the program lead generates one.'}</p>
      ) : null}
    </Panel>
  );
}

function Panel({ children }: { children: ReactNode }) {
  return (
    <section className="gp-sec" aria-labelledby={ASSESSMENT_RESULTS_ANCHOR ?? 'assessment-results'} data-part="assessment-results">
      <h3 id={ASSESSMENT_RESULTS_ANCHOR ?? 'assessment-results'} className="gp-sec-h" tabIndex={-1}>Assessment results</h3>
      {children}
    </section>
  );
}
